// packages/shared-hooks/src/users/useResetUserPassword.ts
// target user; backend responds `{ ok: true }` (see
// `packages/shared/src/schemas/auth.ts` ResetPasswordInputSchema +
// ResetPasswordResponseSchema). No cleartext reveal carry-forward; a
// generate-and-reveal variant is tracked in design-debt.
// Invalidates only `['user', id]` because the list row doesn't visibly
// change (the `passwordResetRequired` flag lives on the detail record).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ResetPasswordInputSchema,
  ResetPasswordResponseSchema,
  type ResetPasswordInput,
  type ResetPasswordResponse,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';

export function useResetUserPassword(id: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ResetPasswordResponse, unknown, ResetPasswordInput>({
    mutationFn: (input) => {
      // WHY: validate client-side before the network round-trip. The
      // server enforces the same shape (.min(12).max(256)), so a too-short
      // password would 400 anyway — failing fast saves the round-trip and
      // gives the UI a typed error instead of a JSON body decode.
      // Send `parsed.data` (not raw `input`) so the schema's `.strict()`
      // semantics strip any extra keys before they hit the wire.
      const parsed = ResetPasswordInputSchema.safeParse(input);
      if (!parsed.success) {
        const issue = parsed.error.issues[0];
        // Construct a payload-free message: path + code + Zod's own
        // human-readable text. Never include `input` or `newPassword`.
        const detail = issue
          ? `${issue.path.join('.') || '<root>'}: ${issue.code}: ${issue.message}`
          : 'invalid input';
        return Promise.reject(new Error(`reset-password input rejected — ${detail}`));
      }
      return client.post<ResetPasswordResponse>(
        `/users/${id}/reset-password`,
        parsed.data,
        {},
        ResetPasswordResponseSchema,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] });
    },
  });
}
